import { useEffect, useRef, useState } from 'react'

import { Skeleton } from '../../Skeleton'
import { Breadcrumb } from '../Breadcrumb'

import { getBreadcrumbsItemsKey, getListItemProps } from './Breadcrumbs.utils'

import * as styles from './Breadcrumbs.module.css'

function Breadcrumbs({
  Item = Breadcrumb,
  className = styles.Breadcrumbs,
  count,
  isLoading,
  itemProps,
  items,
  skeletonClassName = styles.skeleton,
  variant,
}) {
  const [hidden, setHidden] = useState(new Set())

  const itemsKey = getBreadcrumbsItemsKey(items)

  const [prevItemsKey, setPrevItemsKey] = useState(itemsKey)

  const ref = useRef(null)

  function handleResetHidden() {
    setHidden(hidden => (hidden.size > 0 ? new Set() : hidden))
  }

  // Re-flow only when the trail itself changes (navigation, rename), not on
  // every items identity change: a list reload with the same folders must not
  // remount the collapsed "..." dropdown, or its open state (and the user's
  // click) is lost.
  if (itemsKey !== prevItemsKey) {
    setPrevItemsKey(itemsKey)
    handleResetHidden()
  }

  function handleHideItem(id) {
    setHidden(hidden => new Set(hidden).add(id))
  }

  const options = items?.filter(i => !hidden.has(i.id))

  useEffect(() => {
    // ResizeObserver always reports once on observe; only real resizes re-flow
    let isInitialEntry = true

    const ro = new ResizeObserver(() => {
      if (isInitialEntry) {
        isInitialEntry = false
      } else {
        handleResetHidden()
      }
    })

    ro.observe(ref.current)

    return () => {
      ro.disconnect()
    }
  }, [])

  return (
    <div className={className} ref={ref}>
      {isLoading ? (
        <Skeleton className={skeletonClassName} />
      ) : (
        options.map((item, index) => {
          return (
            <Item
              {...getListItemProps(index, options.length - 1)}
              {...itemProps}
              count={count}
              hidden={hidden}
              items={items}
              key={item.id}
              option={item}
              variant={variant}
              onHide={handleHideItem}
            />
          )
        })
      )}
    </div>
  )
}

export default Breadcrumbs
